/**
 * TypeSelector Component
 * Step 1: pick the system to connect.
 */

import React from 'react';

type DatasourceType = 'salesforce' | 'sap_erp' | 'odoo';

interface TypeSelectorProps {
  onSelect: (type: DatasourceType) => void;
}

const types = [
  {
    id: 'salesforce' as DatasourceType,
    name: 'Salesforce',
    kind: 'CRM',
    abbr: 'SF',
    color: '#00A1E0',
    description: 'Pipeline, accounts and won revenue — the commercial side of every answer.',
    features: ['OAuth 2.0', 'REST API', 'Real-time sync'],
  },
  {
    id: 'sap_erp' as DatasourceType,
    name: 'SAP ERP',
    kind: 'ERP',
    abbr: 'SAP',
    color: '#0B66C3',
    description: 'Invoiced revenue, COGS and cash — the system of record for finance.',
    features: ['HTTP / REST', 'Secure auth', 'Company codes'],
  },
  {
    id: 'odoo' as DatasourceType,
    name: 'Odoo',
    kind: 'ERP',
    abbr: 'OD',
    color: '#714B67',
    description: 'Accounting, inventory and manufacturing for subsidiary entities.',
    features: ['XML-RPC', 'Database access', 'Multi-company'],
  },
];

export const TypeSelector: React.FC<TypeSelectorProps> = ({ onSelect }) => {
  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <span className="eye">Step 1</span>
        <h4 style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-.03em' }}>
          Which system are we connecting?
        </h4>
        <p className="dim" style={{ fontSize: 12.5, marginTop: 4 }}>
          OmniMind connects through each vendor's governed interface — never by scraping screens.
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        }}
      >
        {types.map((type) => (
          <button
            key={type.id}
            onClick={() => onSelect(type.id)}
            className="conn click"
            style={{ textAlign: 'left' }}
          >
            <div className="top2">
              <span className="lg" style={{ background: type.color }}>
                {type.abbr}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="n">{type.name}</div>
                <div className="m">{type.kind}</div>
              </div>
            </div>

            <div
              style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 9, lineHeight: 1.45 }}
            >
              {type.description}
            </div>

            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 11 }}>
              {type.features.map((feature) => (
                <span className="tg tg-n" key={feature}>
                  {feature}
                </span>
              ))}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
